import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Building, Ticket, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/apiClient";
import AdminShell from "./AdminShell";

export default function AdminLeadsList() {
  const [items, setItems] = useState([]);
  const [offers, setOffers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  async function load(slug = "all") {
    setLoading(true);
    try {
      const url = slug && slug !== "all" ? `/admin/leads?offer_slug=${encodeURIComponent(slug)}` : "/admin/leads";
      const { data } = await api.get(url);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load leads"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/offers");
        setOffers(data.items || []);
      } catch {
        /* ignore */
      }
      await load("all");
    })();
  }, []);

  async function remove(id) {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted");
      load(filter);
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed"));
    }
  }

  return (
    <AdminShell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-display font-bold text-oj-ink">Leads</h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            {loading ? "Loading..." : `${items.length} lead${items.length === 1 ? "" : "s"}`}
          </p>
        </div>
        <select
          data-testid="admin-leads-filter"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            load(e.target.value);
          }}
          className="rounded-md border border-zinc-300 px-3 py-2 text-sm"
        >
          <option value="all">All offers</option>
          {offers.map((o) => (
            <option key={o.id} value={o.slug}>{o.title}</option>
          ))}
        </select>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="admin-leads-table">
        {items.length === 0 && !loading ? (
          <div className="p-12 text-center text-zinc-500 text-sm">No leads yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Offer</th>
                <th className="text-left px-4 py-3">Voucher</th>
                <th className="text-left px-4 py-3">Received</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <React.Fragment key={l.id}>
                  <tr
                    className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                    onClick={() => setExpanded(expanded === l.id ? null : l.id)}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-oj-ink">{l.contact?.full_name}</div>
                      <div className="text-xs text-zinc-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        <span className="inline-flex items-center gap-1"><Mail className="size-3" /> {l.contact?.email}</span>
                        <span className="inline-flex items-center gap-1"><Phone className="size-3" /> {l.contact?.phone}</span>
                        {l.contact?.business_name && (
                          <span className="inline-flex items-center gap-1"><Building className="size-3" /> {l.contact.business_name}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/offers/${l.offer_slug}`} target="_blank" className="text-oj-ink hover:underline">
                        {l.offer_title}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {l.voucher_code ? (
                        <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">
                          <Ticket className="size-3" /> {l.voucher_code}
                        </span>
                      ) : (
                        <span className="text-zinc-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500">
                      {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); remove(l.id); }}
                        className="p-1.5 rounded hover:bg-red-50"
                      >
                        <Trash2 className="size-4 text-red-500" />
                      </button>
                    </td>
                  </tr>
                  {expanded === l.id && (
                    <tr className="bg-zinc-50 border-t border-zinc-100">
                      <td colSpan={5} className="px-4 py-4">
                        <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-2">Answers</div>
                        {Object.keys(l.answers || {}).length === 0 ? (
                          <div className="text-sm text-zinc-500 italic">No custom answers.</div>
                        ) : (
                          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                            {Object.entries(l.answers).map(([k, v]) => (
                              <div key={k}>
                                <dt className="text-xs font-semibold text-zinc-500">{k}</dt>
                                <dd className="text-oj-ink break-words">
                                  {Array.isArray(v) ? v.join(", ") : String(v ?? "")}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
}
